import React, { useState } from "react";
import {
  buildMonthDays,
  compareCalendarRows,
  getDayNumberCircleClasses,
  getDayPrimaryStatus,
  getRowDisplayStatus,
  getStatusColors,
  rowsOnDay,
  sameDay,
} from "../../utils/assessmentCalendarRules";
import { getMonthDayCellClasses } from "../../utils/calendarDayCell";
import CalendarDayCell from "./CalendarDayCell";

const WEEKDAYS = ["S", "M", "T", "W", "Th", "F", "S"];

function MonthDayDots({ dayRows, dayPrimaryStatus, isAdmin, today }) {
  return (
    <span className="absolute bottom-2 left-1/2 -translate-x-1/2 transform">
      <span className="flex items-center space-x-1">
        {[...dayRows]
          .sort(compareCalendarRows)
          .slice(0, 3)
          .map((assessment, idx) => {
            const rowStatus = getRowDisplayStatus(assessment, { isAdmin, today });
            if (!rowStatus) return null;
            return (
              <span
                key={`${assessment.vqa_id}-${assessment.sponsor_id}-${idx}`}
                className={`block h-1.5 w-1.5 rounded-full ${getStatusColors(rowStatus).dot}`}
              />
            );
          })}
        {dayRows.length > 3 ? (
          <span className={`text-xs font-bold ${getStatusColors(dayPrimaryStatus).dot.replace("bg-", "text-")}`}>
            +
          </span>
        ) : null}
      </span>
    </span>
  );
}

const CalendarMonthGrid = ({
  monthDate,
  rows,
  isAdmin,
  today = new Date(),
  selectedDate,
  onSelectDate,
}) => {
  const [hoverKey, setHoverKey] = useState(null);
  const days = buildMonthDays(monthDate);

  return (
    <div>
      <div className="grid grid-cols-7 text-center text-xs leading-6 text-gray-500">
        {WEEKDAYS.map((label) => (
          <div key={label}>{label}</div>
        ))}
      </div>
      <div className="relative isolate z-20 mt-2 grid grid-cols-7 gap-px overflow-visible rounded-lg bg-gray-200 text-sm shadow ring-1 ring-gray-200">
        {days.map((day, dayIdx) => {
          const dayRows = rowsOnDay(rows, day.date);
          const dayPrimaryStatus = getDayPrimaryStatus(dayRows, { isAdmin, today });
          const selected = sameDay(day.date, selectedDate);
          const isToday = sameDay(day.date, today);

          return (
            <CalendarDayCell
              key={day.key}
              date={day.date}
              dayIdx={dayIdx}
              rows={dayRows}
              selected={selected}
              isAdmin={isAdmin}
              today={today}
              isHovered={hoverKey === day.key}
              onHoverChange={(hovered) => setHoverKey(hovered ? day.key : null)}
              onSelectDate={onSelectDate}
              className={getMonthDayCellClasses({
                inMonth: day.inMonth,
                isToday,
                dayIdx,
                totalDays: days.length,
              })}
            >
              <time
                dateTime={day.date.toISOString().slice(0, 10)}
                className={getDayNumberCircleClasses(dayPrimaryStatus, {
                  selected,
                  isToday,
                  hasAssessments: dayRows.length > 0,
                })}
              >
                {day.date.getDate()}
              </time>
              {dayRows.length > 0 ? (
                <MonthDayDots
                  dayRows={dayRows}
                  dayPrimaryStatus={dayPrimaryStatus}
                  isAdmin={isAdmin}
                  today={today}
                />
              ) : null}
            </CalendarDayCell>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarMonthGrid;
